/**
 * PIN Login — US-E1-003 part 3, redesign 2026-05-09 (banking pattern).
 *
 * UX (как iOS Lock / Avast / Тинькофф):
 *  - Без поля email — показываем masked email как контекст («Войти как g***@gmail.com»)
 *  - 4 dots fill animation
 *  - Custom NumPad (3×4) для touch-устройств
 *  - Auto-submit на 4й цифре
 *  - Shake + clear на wrong PIN
 *
 * Edge cases:
 *  - 401 pin_wrong         → toast + shake + clear, allow retry
 *  - 403 pin_reset_required → 5 fails → /auth/login?reason=pin_reset
 *  - 404 pin_not_found     → /auth/login?reason=no_pin
 *  - 410 pin_expired       → /auth/login?reason=pin_expired
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { ApiError } from '@/api/client'
import { useAuth } from '@/stores/auth'
import { redirectAfterAuth } from '@/lib/authRedirect'
import { getDeviceId } from '@/lib/deviceId'
import { useIsPinCapable } from '@/lib/device'
import { Icon } from '@/components/icons/Icon'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { PinDots } from '@/components/auth/PinDots'
import { NumPad } from '@/components/auth/NumPad'
import { toast } from '@/components/ui/toast'
import { validateEmail } from '@/lib/validation'

const LAST_EMAIL_KEY = 'agentceo_last_login_email'
const PIN_LENGTH = 4

/** Mask email like "alex@company.ru" → "a***@company.ru" */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!local || !domain) return email
  return `${local.slice(0, 1)}***@${domain}`
}

export function PinLogin() {
  const { t } = useTranslation()
  const setSession = useAuth((s) => s.setSession)
  const isPinCapable = useIsPinCapable()

  // Phase 1: PIN flow только на mobile/tablet (touch). На desktop — redirect.
  if (typeof window !== 'undefined' && !isPinCapable) {
    window.location.replace('/auth/login?reason=pin_desktop_disabled')
    return null
  }

  // Email из URL ?email= (после reset/expired) или из last-login localStorage
  const initialEmail = (() => {
    if (typeof window === 'undefined') return ''
    const fromUrl = new URLSearchParams(window.location.search).get('email')
    if (fromUrl) return fromUrl
    return localStorage.getItem(LAST_EMAIL_KEY) ?? ''
  })()

  const [email] = useState(initialEmail)
  const [pin, setPin] = useState('')
  const [errorState, setErrorState] = useState(false)
  const [successState, setSuccessState] = useState(false)

  const m = useMutation({
    mutationFn: (body: { email: string; pin: string; device_id: string }) =>
      authApi.pinVerify(body),
    onSuccess: (res) => {
      setSession(res)
      localStorage.setItem(LAST_EMAIL_KEY, res.user.email)
      // Green flash 300ms — positive feedback перед redirect, чтобы юзер
      // успел увидеть «PIN принят» вместо мгновенного исчезновения экрана.
      setSuccessState(true)
      setTimeout(() => {
        redirectAfterAuth(res.user)
      }, 350)
    },
    onError: (e: ApiError) => {
      // Shake + clear PIN, оставляем юзера на странице для retry
      setErrorState(true)
      setTimeout(() => {
        setPin('')
        setErrorState(false)
      }, 360) // sync с длиной shake animation

      if (e.code === 'pin_reset_required' || e.status === 403) {
        toast.warning(
          t('auth.pin.reset_title', 'PIN сброшен'),
          t('auth.pin.reset_body', 'Слишком много неверных попыток. Войди полным паролем.'),
        )
        setTimeout(() => {
          window.location.href = `/auth/login?email=${encodeURIComponent(email)}&reason=pin_reset`
        }, 1500)
      } else if (e.code === 'pin_expired' || e.status === 410) {
        toast.warning(
          t('auth.pin.expired_title', 'PIN истёк'),
          t('auth.pin.expired_body', 'Давно не заходил с этого устройства — нужен полный пароль.'),
        )
        setTimeout(() => {
          window.location.href = `/auth/login?email=${encodeURIComponent(email)}&reason=pin_expired`
        }, 1500)
      } else if (e.code === 'pin_not_found' || e.status === 404) {
        toast.info(
          t('auth.pin.not_found_title', 'PIN не настроен'),
          t('auth.pin.not_found_body', 'На этом устройстве нет PIN — войди паролем.'),
        )
        setTimeout(() => {
          window.location.href = `/auth/login?email=${encodeURIComponent(email)}&reason=no_pin`
        }, 1500)
      } else if (e.status === 401) {
        toast.error(
          t('auth.pin.wrong_title', 'Не совпало'),
          t('auth.pin.wrong_body', 'Попробуй ещё раз. После 5 ошибок PIN сбросится.'),
        )
      } else {
        toast.error(
          t('auth.pin.network_title', 'Ошибка сети'),
          t('auth.pin.network_body', 'Не удалось войти по PIN. Попробуй ещё раз.'),
        )
      }
    },
  })

  // Auto-submit когда PIN заполнен (4 цифры)
  useEffect(() => {
    if (pin.length === PIN_LENGTH && !m.isPending && !errorState && !successState) {
      if (!email || !validateEmail(email)) {
        toast.warning(t('auth.pin.email_required', 'Не нашёл сохранённый email — войди паролем'))
        setPin('')
        return
      }
      m.mutate({ email: email.toLowerCase().trim(), pin, device_id: getDeviceId() })
    }
  }, [pin, m, email, errorState, successState, t])

  const onDigit = (d: string) => {
    if (m.isPending || errorState || successState) return
    setPin((p) => (p.length >= PIN_LENGTH ? p : p + d))
  }
  const onDelete = () => {
    if (m.isPending || errorState || successState) return
    setPin((p) => p.slice(0, -1))
  }

  return (
    <AuthLayout side="login">
      <a
        href="/auth/login"
        className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary text-sm font-medium mb-8"
      >
        <Icon name="arrow-left" size={16} />
        {t('auth.pin.back_to_login', 'Войти паролем')}
      </a>

      <div className="text-center space-y-2 mb-8">
        <h1 className="text-3xl font-bold font-display text-text-primary">
          {t('auth.pin.title', 'С возвращением')}
        </h1>
        {email && validateEmail(email) ? (
          <p className="text-sm text-text-secondary">
            {t('auth.pin.sub_user', 'Войти как')}{' '}
            <span className="font-semibold text-text-primary">{maskEmail(email)}</span>
          </p>
        ) : (
          <p className="text-sm text-text-secondary">
            {t('auth.pin.sub_no_email', 'Введи PIN-код')}
          </p>
        )}
      </div>

      <div className="my-10">
        <PinDots
          length={PIN_LENGTH}
          filled={pin.length}
          error={errorState}
          success={successState}
        />
      </div>

      <NumPad
        onDigit={onDigit}
        onDelete={onDelete}
        disabled={m.isPending || errorState || successState}
      />

      <p className="text-center text-sm text-text-secondary mt-10">
        <a
          href="/auth/login"
          className="inline-flex items-center min-h-[24px] px-1 text-text-link font-semibold"
        >
          {t('auth.pin.use_password', 'Не помню PIN — войти паролем')}
        </a>
      </p>
    </AuthLayout>
  )
}
